import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef } from "react";
import Navbar from "./Navbar";
import { motion } from "framer-motion";

const Menu = ({ searchTerm, isDarkMode }) => {
  const { t, i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState("new");
  const sectionRefs = useRef({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isScrolling, setIsScrolling] = useState(false);

  const menuCategories = t("menu", { returnObjects: true });
  const products = t("products", { returnObjects: true });

  useEffect(() => {
    const handleScroll = () => {
      if (isScrolling) return;
      const scrollPosition = window.scrollY + 150;
      let newActiveCategory = activeCategory;

      Object.keys(sectionRefs.current).forEach((key) => {
        const section = sectionRefs.current[key];
        if (section && scrollPosition >= section.offsetTop) {
          newActiveCategory = key;
        }
      });

      if (newActiveCategory !== activeCategory) {
        setActiveCategory(newActiveCategory);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeCategory, isScrolling]);

  const handleNavClick = (category) => {
    setIsScrolling(true);
    setActiveCategory(category);
    const element = sectionRefs.current[category];
    if (element) {
      const yOffset = -120;
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
      setTimeout(() => setIsScrolling(false), 1000);
    }
  };

  const filterProducts = (categoryName) => {
    let filtered = products.filter(
      (p) =>
        p?.category &&
        p.category.toLowerCase().startsWith(categoryName?.toLowerCase() || ""),
    );

    if (searchTerm) {
      filtered = filtered.filter(
        (p) =>
          (p?.name &&
            p.name.toLowerCase().includes(searchTerm?.toLowerCase() || "")) ||
          (p?.description &&
            p.description
              .toLowerCase()
              .includes(searchTerm?.toLowerCase() || "")),
      );
    }

    return filtered;
  };

  const groupProducts = (products) => {
    return products.reduce((acc, product) => {
      const group = product.description_category || "other";
      if (!acc[group]) acc[group] = [];
      acc[group].push(product);
      return acc;
    }, {});
  };

  const dessertNoteText = {
    ar: "تشكيلة الحلويات لدينا تتغير يوميًا لنقدم لكم دائمًا أشهى المأكولات الطازجة والموسمية. يُرجى الاستفسار عند الكاشير عن التشكيلة المتوفرة حاليًا.",
    en: "Our dessert selection varies daily to always offer you fresh and seasonal delicacies. Please ask at the counter for today's specialties.",
    de: "Unser Dessertangebot variiert täglich, um Ihnen stets frische und saisonale Köstlichkeiten bieten zu können. Bitte erkundigen Sie sich an der Theke nach den aktuellen Spezialitäten.",
  };
  const sandwichNoteText = {
    ar: `السندويتشات متاحة يوميًا من الساعة 10:00 إلى 15:00.
بعد الساعة 15:00 تكون متوفرة عند الطلب فقط.`,

    en: `Sandwiches are available daily from 10:00 AM to 3:00 PM.
After 3:00 PM, they are available upon request only.`,

    de: `Sandwiches täglich von 10:00 bis 15:00 Uhr verfügbar.
Ab 15:00 Uhr nur auf Anfrage`,
  };

  const specialtyNoteText = {
    ar: `اسألنا عن توفر هذه المنتجات`,
    en: "Ask us about the availability of these products",
    de: "Fragen Sie uns nach der Verfügbarkeit dieser Produkte",
  };
  // ألوان الوضع الفاتح (فضي/أزرق موحد)
  const lightTheme = {
    bgPrimary: "#f8f9fadf",
    bgSecondary: "#ffffff",
    textPrimary: "#2c3e50",
    textSecondary: "#5d6d7e",
    border: "#FFC107",
    accent: "#3498db",
    primary: "#e67e22",
    secondary: "#e67e22",
    warning: "#e67e22",
    success: "#27ae60",
    silver: "#95a5a6",
    gold: "#e67e22",
  };

  // ألوان الوضع الداكن (الأصلية)
  const darkTheme = {
    bgPrimary: "#00000058",
    bgSecondary: "#3b252576",
    textPrimary: "#ffffff",
    textSecondary: "#FFC107",
    border: "#d2a70c",
    accent: "#ffe600",
    primary: "#FFC107",
    secondary: "#FFC107",
    warning: "#FFC107",
    success: "#27ae60",
    silver: "#FFC107",
    gold: "#FFC107",
  };

  const currentTheme = isDarkMode ? darkTheme : lightTheme;

  return (
    <div
      className="position-relative"
      style={{
        backgroundColor: currentTheme.bgPrimary,
        minHeight: "100vh",
        color: currentTheme.textPrimary,
      }}
    >
      <Navbar
        activeCategory={activeCategory}
        onCategoryClick={handleNavClick}
        isDarkMode={isDarkMode}
      />

      <div className="container">
        {Object.entries(menuCategories).map(([key, name]) => {
          const categoryProducts = filterProducts(name);
          if (categoryProducts.length === 0) return null;

          const groupedProducts = groupProducts(categoryProducts);

          return (
            <section
              key={key}
              id={key}
              ref={(el) => (sectionRefs.current[key] = el)}
              className="pt-3 mb-5"
            >
              <h5
                className="rounded-3 mb-2"
                style={{
                  padding: "5px 16px",
                  background: isDarkMode ? "#000000" : currentTheme.primary,
                  border: `1px solid ${currentTheme.primary}`,
                  display: "inline-block",
                  color: isDarkMode ? currentTheme.primary : "#ffffff",
                  fontWeight: "bold",
                  textShadow: isDarkMode ? "none" : "0 1px 2px rgba(0,0,0,0.1)",
                }}
              >
                {name}
              </h5>

              {key === "dessert" && (
                <p
                  className="border rounded my-3 text-center py-2 fs-3"
                  style={{
                    borderColor: currentTheme.primary,
                    color: currentTheme.textPrimary,
                    backgroundColor: isDarkMode
                      ? "#000000"
                      : "rgba(52, 152, 219, 0.1)",
                    fontWeight: "500",
                  }}
                >
                  {dessertNoteText[i18n.language] || dessertNoteText.en}
                </p>
              )}
              {key === "Sandwiches" && (
                <p
                  className="border rounded my-3 text-center py-2 fs-3"
                  style={{
                    borderColor: currentTheme.primary,
                    color: currentTheme.textPrimary,
                    backgroundColor: isDarkMode
                      ? "#000000"
                      : "rgba(52, 152, 219, 0.1)",
                    fontWeight: "500",
                  }}
                >
                  {sandwichNoteText[i18n.language] || sandwichNoteText.en}
                </p>
              )}

              {key === "specialty" && (
                <p
                  className="border rounded my-3 text-center py-2 fs-3"
                  style={{
                    borderColor: currentTheme.primary,
                    color: currentTheme.textPrimary,
                    backgroundColor: isDarkMode
                      ? "#000000"
                      : "rgba(52, 152, 219, 0.1)",
                    fontWeight: "500",
                  }}
                >
                  {specialtyNoteText[i18n.language] || specialtyNoteText.en}
                </p>
              )}

              {Object.entries(groupedProducts).map(([groupName, products]) => (
                <div key={groupName} className="mb-4">
                  {groupName !== "other" && (
                    <h3
                      className="mb-3 px-2 py-1"
                      style={{
                        background: isDarkMode
                          ? "#111111"
                          : currentTheme.primary,
                        borderLeft: `3px solid ${currentTheme.secondary}`,
                        borderRight: `3px solid ${currentTheme.secondary}`,
                        display: "inline-block",
                        borderRadius: "5px",
                        color: "#ffffff",
                        fontWeight: "bold",
                        textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                      }}
                    >
                      {groupName}
                    </h3>
                  )}

                  <div className="row g-3">
                    {products.map((product) => (
                      <motion.div
                        key={product.id}
                        className="col-md-6"
                        initial={{
                          opacity: 0,
                          y: 100,
                        }}
                        whileInView={{
                          opacity: 1,
                          y: 0,
                        }}
                        viewport={{ once: false }}
                        transition={{
                          type: "spring",
                          stiffness: 120,
                          damping: 8,
                        }}
                      >
                        <div
                          className="h-100 p-3 rounded-4 d-flex align-items-center justify-content-between gap-3"
                          onClick={() => setSelectedProduct(product)}
                          style={{
                            cursor: "pointer",
                            backgroundColor: currentTheme.bgSecondary,
                            border: `1px solid ${currentTheme.border}`,
                            boxShadow: isDarkMode
                              ? "0 5px 20px rgba(210,167,12,.08)"
                              : "0 5px 20px rgba(0,0,0,.08)",
                            transition: "all .3s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-5px)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                          }}
                          data-bs-toggle="modal"
                          data-bs-target="#productModal"
                        >
                          <div className="flex-grow-1">
                            <h4
                              className="mb-2"
                              style={{
                                color: currentTheme.secondary,
                                fontWeight: "700",
                                letterSpacing: ".3px",
                              }}
                            >
                              {product.name}
                            </h4>

                            <p
                              className="mb-2"
                              style={{
                                color: currentTheme.textSecondary,
                                lineHeight: "1.6",
                                fontSize: ".95rem",
                                overflow: "hidden",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                              }}
                            >
                              {product.description}
                            </p>

                            {product.secDes && (
                              <h6
                                className="mb-2"
                                style={{
                                  color: currentTheme.textPrimary,
                                  fontWeight: "600",
                                }}
                              >
                                {product.secDes}
                              </h6>
                            )}

                            <div
                              className="d-inline-flex align-items-center px-3 py-1 rounded-pill"
                              style={{
                                background: isDarkMode
                                  ? "rgba(210,167,12,.12)"
                                  : `${currentTheme.primary}15`,
                              }}
                            >
                              <span
                                style={{
                                  color: currentTheme.secondary,
                                  fontWeight: "700",
                                  fontSize: "1.15rem",
                                }}
                              >
                                {product.price}
                              </span>

                              {product.doublePrice && (
                                <span
                                  className="ms-3"
                                  style={{
                                    color: currentTheme.secondary,
                                    fontWeight: "700",
                                  }}
                                >
                                  2x {product.doublePrice}
                                </span>
                              )}
                            </div>
                          </div>

                          {product.image && (
                            <div
                              style={{
                                position: "relative",
                                width: "110px",
                                minWidth: "110px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <img
                                src={product.image}
                                alt={product.name}
                                className="rounded-circle"
                                style={{
                                  width: "95px",
                                  height: "95px",
                                  objectFit: "cover",
                                  border: `3px solid ${currentTheme.secondary}`,
                                  boxShadow: isDarkMode
                                    ? "0 0 15px rgba(210,167,12,.2)"
                                    : "0 3px 10px rgba(0,0,0,.15)",
                                }}
                              />

                              {product.comingSoon && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "110%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    backgroundColor: isDarkMode
                                      ? "rgba(0,0,0,.95)"
                                      : "rgba(255,255,255,.95)",
                                    color: currentTheme.secondary,
                                    padding: "5px 8px",
                                    borderRadius: "5px",
                                    fontSize: ".75rem",
                                    textAlign: "center",
                                    whiteSpace: "nowrap",
                                    fontWeight: "bold",
                                    border: `1px solid ${currentTheme.secondary}`,
                                  }}
                                >
                                  {product.comingSoon}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          );
        })}
      </div>

      {/* ==== Bootstrap Modal ==== */}
      <div
        className="modal fade"
        id="productModal"
        tabIndex="-1"
        aria-labelledby="productModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div
            className="modal-content"
            style={{
              backgroundColor: currentTheme.bgSecondary,
              color: currentTheme.textPrimary,
              border: `2px solid ${currentTheme.primary}`,
            }}
          >
            <div className="modal-header border-0">
              <h5
                className="modal-title"
                id="productModalLabel"
                style={{
                  color: currentTheme.secondary,
                  fontWeight: "bold",
                }}
              >
                {selectedProduct?.name}
              </h5>
              <button
                type="button"
                className="btn-close"
                style={{
                  filter: isDarkMode ? "invert(1)" : "none",
                  backgroundColor: currentTheme.secondary,
                }}
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <p
                style={{
                  color: currentTheme.textSecondary,
                  fontWeight: "300",
                }}
              >
                {selectedProduct?.description}
              </p>
              <p>
                <strong style={{ color: currentTheme.textPrimary }}>
                  {t("Price")}:
                </strong>
                <span
                  style={{
                    color: currentTheme.secondary,
                    fontWeight: "bold",
                  }}
                >
                  {" "}
                  {selectedProduct?.price}
                </span>
              </p>
              {selectedProduct?.doublePrice && (
                <p>
                  <strong style={{ color: currentTheme.textPrimary }}>
                    2x:
                  </strong>
                  <span
                    style={{
                      color: currentTheme.secondary,
                      fontWeight: "",
                    }}
                  >
                    {" "}
                    {selectedProduct.doublePrice}
                  </span>
                </p>
              )}
              {selectedProduct?.comingSoon && (
                <p
                  style={{
                    color: currentTheme.secondary,
                    fontWeight: "bold",
                  }}
                >
                  <strong>{selectedProduct.comingSoon}</strong>
                </p>
              )}
              {selectedProduct?.image && (
                <img
                  src={selectedProduct.image}
                  alt={selectedProduct.name}
                  className="img-fluid rounded"
                  style={{
                    // border: `2px solid ${currentTheme.border}`,
                    height : '400px',
                    width : '100%',
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Menu;
